import React, { useEffect, useState } from "react";
import ComplianceLock from "@/components/compliance/ComplianceLock";

/**
 * CareLogix™ ORACLE — Global Intelligence Grid (Phase 27G).
 *
 * SVG world map (CareLogix-native abstract) with 10 region markers pulsing
 * LIVE. Includes 195 countries / 24 timezones / global markets online badges.
 */

const REGIONS = [
  { id: "na-east",  label: "North America · East",   x: 25,  y: 38 },
  { id: "na-west",  label: "North America · West",   x: 17,  y: 41 },
  { id: "sa",       label: "South America",           x: 30,  y: 70 },
  { id: "emea-uk",  label: "Europe · UK",             x: 47,  y: 32 },
  { id: "emea-eu",  label: "Europe · EU",             x: 50,  y: 35 },
  { id: "africa",   label: "Africa",                  x: 52,  y: 60 },
  { id: "mena",     label: "Middle East / GCC",       x: 60,  y: 45 },
  { id: "sasia",    label: "South Asia",              x: 68,  y: 50 },
  { id: "easia",    label: "East Asia",               x: 79,  y: 42 },
  { id: "oceania",  label: "Oceania",                 x: 86,  y: 73 },
];

export default function OracleGlobalIntelGrid({ testid = "oracle-global-intel-grid" }) {
  const [tick, setTick] = useState(0);
  const [marketsOnline, setMarketsOnline] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1800);
    return () => clearInterval(id);
  }, []);
  // Markets-online counter ticks across regions over time.
  useEffect(() => {
    setMarketsOnline(Math.min(REGIONS.length, 4 + (tick % REGIONS.length)));
  }, [tick]);

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <div>
          <span style={S.tagline}>Global Intelligence Grid™</span>
          <h2 style={S.title}>🌎 ORACLE Global Markets · LIVE</h2>
        </div>
        <div style={S.badges}>
          <span style={S.badge} data-testid={`${testid}-badge-countries`}>195 countries</span>
          <span style={S.badge} data-testid={`${testid}-badge-timezones`}>24 time zones</span>
          <span style={{ ...S.badge, color: "#10b981", borderColor: "#10b981" }} data-testid={`${testid}-badge-markets`}>
            ● {marketsOnline}/{REGIONS.length} markets online
          </span>
        </div>
      </header>

      <div style={S.mapWrap}>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={S.svg} aria-hidden="true">
          <defs>
            <radialGradient id="oig-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#c5a05966" />
              <stop offset="60%" stopColor="#c5a05922" />
              <stop offset="100%" stopColor="transparent" />
            </radialGradient>
            <linearGradient id="oig-land" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%"  stopColor="#0b3d2e" />
              <stop offset="100%" stopColor="#082016" />
            </linearGradient>
          </defs>
          {/* Background grid */}
          {Array.from({ length: 20 }).map((_, i) => (
            <line key={`gv-${i}`} x1={i * 5} y1="0" x2={i * 5} y2="100" stroke="rgba(197,160,89,0.05)" strokeWidth="0.1" />
          ))}
          {Array.from({ length: 12 }).map((_, i) => (
            <line key={`gh-${i}`} x1="0" y1={i * 8.33} x2="100" y2={i * 8.33} stroke="rgba(197,160,89,0.05)" strokeWidth="0.1" />
          ))}
          {/* Abstract land-mass blobs (CareLogix original — not a real map) */}
          <path d="M5 30 Q20 25 35 35 T55 50 Q40 55 28 65 T8 60 Z" fill="url(#oig-land)" opacity="0.85" />
          <path d="M25 65 Q35 60 42 80 T22 90 Z" fill="url(#oig-land)" opacity="0.80" />
          <path d="M40 25 Q55 22 65 35 T55 55 Q47 50 42 38 Z" fill="url(#oig-land)" opacity="0.85" />
          <path d="M48 55 Q60 50 62 75 T46 78 Z" fill="url(#oig-land)" opacity="0.80" />
          <path d="M62 32 Q78 28 90 38 T90 60 Q80 56 68 60 T58 48 Z" fill="url(#oig-land)" opacity="0.85" />
          <path d="M80 70 Q90 67 92 80 T80 82 Z" fill="url(#oig-land)" opacity="0.80" />
          <circle cx="50" cy="50" r="55" fill="url(#oig-glow)" />
          {REGIONS.map((r, i) => {
            const phase = (tick + i) % 4;
            const rad = 0.9 + phase * 0.35;
            return (
              <g key={r.id}>
                <circle cx={r.x} cy={r.y} r={2.5 + phase * 0.4} fill="#10b981" opacity={0.35} />
                <circle cx={r.x} cy={r.y} r={rad} fill="#10b981">
                  <animate attributeName="r" values="0.8;1.6;0.8" dur="1.8s" repeatCount="indefinite" />
                </circle>
              </g>
            );
          })}
        </svg>
        <div style={S.legend} aria-label="Live region markers">
          {REGIONS.map((r) => (
            <span key={r.id} style={S.legendChip} data-testid={`${testid}-region-${r.id}`}>
              <span style={S.legendDot} /> {r.label}
            </span>
          ))}
        </div>
      </div>

      <ComplianceLock variant="inline" short policy="v27" testid={`${testid}-compliance`} />
    </section>
  );
}

const S = {
  shell: { background: "rgba(11,15,20,0.94)", border: "1px solid rgba(197,160,89,0.22)", borderRadius: 14, padding: 14, color: "#e2e8f0", display: "flex", flexDirection: "column", gap: 10, fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  head: { display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" },
  tagline: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.18em" },
  title: { color: "#fde68a", margin: 0, fontSize: 18 },
  badges: { display: "flex", gap: 6, flexWrap: "wrap" },
  badge: { padding: "5px 11px", border: "1px solid #c5a059", borderRadius: 999, fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.10em", fontWeight: 800, color: "#c5a059" },
  mapWrap: { display: "flex", flexDirection: "column", gap: 8 },
  svg: { width: "100%", height: "clamp(180px, 36vw, 320px)", background: "radial-gradient(circle at center, rgba(16,185,129,0.08), #04060866 60%, #020304 100%)", borderRadius: 12, border: "1px solid rgba(255,255,255,0.05)" },
  legend: { display: "flex", flexWrap: "wrap", gap: 6 },
  legendChip: { display: "inline-flex", alignItems: "center", gap: 6, background: "#040608", border: "1px solid rgba(255,255,255,0.06)", padding: "3px 9px", borderRadius: 999, color: "#cbd5e1", fontFamily: "'Space Mono', monospace", fontSize: 10.5 },
  legendDot: { display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#10b981", boxShadow: "0 0 8px #10b981aa" },
};
